import type { ParsedContractRow } from "../types";
import { ensureScores, getCachedScore, initScoreProvider } from "./scores";
import { showToast } from "./toast";

const SCORE_HEADER = "sfe-score-header";
const SCORE_CELL = "sfe-score-cell";
let scoreProviderReady = false;

function ensureScoreProvider(): void {
  if (scoreProviderReady) return;
  scoreProviderReady = true;
  initScoreProvider(() => repaintScoreColumn());
}

function parseNumber(text: string): number {
  const cleaned = text.replace(/[%$,]/g, "").trim();
  const value = parseFloat(cleaned);
  return Number.isFinite(value) ? value : 0;
}

function cellText(row: HTMLTableRowElement, index: number): string {
  if (index < 0) return "";
  const cells = row.querySelectorAll("td, th");
  return cells[index]?.textContent?.trim() ?? "";
}

interface ScreenerColumnMap {
  ticker: number;
  sector: number;
  strike: number;
  price: number;
  target: number;
  rubricBand: number;
  expiry: number;
  dte: number;
  mid: number;
  collateral: number;
  annRoi: number;
  pop: number;
  held: number;
}

/** Map column indices from thead labels so injected Score (left of Rubric
 * Band) doesn't break fixed-index parsing. */
function getScreenerColumnMap(table: HTMLTableElement): ScreenerColumnMap {
  const fallback: ScreenerColumnMap = {
    ticker: 0,
    sector: 1,
    strike: 2,
    price: 3,
    target: 4,
    rubricBand: 5,
    expiry: 6,
    dte: 7,
    mid: 8,
    collateral: 9,
    annRoi: 10,
    pop: 11,
    held: 12,
  };

  const headers = table.querySelectorAll("thead tr th");
  if (!headers.length) return fallback;

  const map = { ...fallback };
  headers.forEach((th, i) => {
    const t = (th.textContent ?? "").trim().toLowerCase();
    if (t.startsWith("ticker")) map.ticker = i;
    else if (t.startsWith("sector")) map.sector = i;
    else if (t.startsWith("strike")) map.strike = i;
    else if (t === "price") map.price = i;
    else if (t.startsWith("target")) map.target = i;
    else if (t.includes("rubric")) map.rubricBand = i;
    else if (t.startsWith("expiry") || t === "exp") map.expiry = i;
    else if (t === "dte") map.dte = i;
    else if (t === "mid") map.mid = i;
    else if (t.includes("collat")) map.collateral = i;
    else if (/ann\.?\s*roi/.test(t)) map.annRoi = i;
    else if (t === "pop") map.pop = i;
    else if (t.startsWith("held") || t.includes("open")) map.held = i;
  });
  return map;
}

function findRubricBandHeader(table: HTMLTableElement): HTMLTableCellElement | null {
  for (const th of table.querySelectorAll("thead tr th")) {
    if (th.classList.contains(SCORE_HEADER)) continue;
    if (/rubric\s*band/i.test(th.textContent ?? "")) {
      return th instanceof HTMLTableCellElement ? th : null;
    }
  }
  return null;
}

/** Body rows lag the header by one column until our Score td is inserted —
 * locate the native Rubric Band cell reliably for insert + color sync. */
function findBodyRubricCell(
  row: HTMLTableRowElement,
  table: HTMLTableElement,
): HTMLTableCellElement | null {
  const scoreCell = row.querySelector(`.${SCORE_CELL}`);
  if (scoreCell?.nextElementSibling instanceof HTMLTableCellElement) {
    return scoreCell.nextElementSibling;
  }

  const rubricHeader = findRubricBandHeader(table);
  if (!rubricHeader?.parentElement) return null;

  const headerRubricIndex = Array.from(rubricHeader.parentElement.children).indexOf(
    rubricHeader,
  );
  const rowCells = row.querySelectorAll("td, th");
  const scoreInHeader = Boolean(table.querySelector(`.${SCORE_HEADER}`));
  const bodyRubricIndex =
    scoreInHeader && !scoreCell ? headerRubricIndex - 1 : headerRubricIndex;

  const cell = rowCells[bodyRubricIndex];
  return cell instanceof HTMLTableCellElement ? cell : null;
}

/** Ticker cell often includes index badges (NDX, SPX) — take only the symbol. */
function parseTickerFromRow(row: HTMLTableRowElement, tickerCol: number): string {
  const cell = row.querySelectorAll("td, th")[tickerCol];
  if (!(cell instanceof HTMLElement)) return "";

  const link = cell.querySelector("a");
  const linkText = link?.textContent?.trim();
  if (linkText) {
    const m = linkText.match(/^([A-Z]{1,6})\b/);
    if (m) return m[1];
  }

  const text = cell.textContent?.trim() ?? "";
  const m = text.match(/^([A-Z]{1,6})\b/);
  return m ? m[1] : text.split(/\s+/)[0]?.toUpperCase() ?? "";
}

/**
 * Parse screener rows using thead labels (stable when Score column is injected).
 */
export function parseScreenerRows(
  table: HTMLTableElement,
): ParsedContractRow[] {
  const cols = getScreenerColumnMap(table);
  const bodyRows = Array.from(table.querySelectorAll("tbody tr"));
  return bodyRows
    .map((row) => {
      if (!(row instanceof HTMLTableRowElement)) return null;
      const ticker = parseTickerFromRow(row, cols.ticker);
      if (!ticker || ticker === "—") return null;

      return {
        element: row,
        ticker,
        sector: cellText(row, cols.sector),
        strike: parseNumber(cellText(row, cols.strike)),
        price: parseNumber(cellText(row, cols.price)),
        target: parseNumber(cellText(row, cols.target)),
        rubricBand: cellText(row, cols.rubricBand),
        expiry: cellText(row, cols.expiry),
        dte: parseNumber(cellText(row, cols.dte)),
        mid: parseNumber(cellText(row, cols.mid)),
        collateral: parseNumber(cellText(row, cols.collateral)),
        annRoi: parseNumber(cellText(row, cols.annRoi)),
        pop: parseNumber(cellText(row, cols.pop)),
        held: parseNumber(cellText(row, cols.held)),
      } satisfies ParsedContractRow;
    })
    .filter((row): row is ParsedContractRow => row !== null);
}

export function computeScore(roi: number, pop: number): number {
  return Math.round((roi * pop) / 100);
}

export function findScreenerTable(): HTMLTableElement | null {
  const headings = Array.from(document.querySelectorAll("h2, h3, h4, [class*='title']"));
  const screenerHeading = headings.find((el) =>
    /put contract screener|call contract screener/i.test(el.textContent ?? ""),
  );

  if (screenerHeading) {
    const container = screenerHeading.closest("section, div, article") ?? screenerHeading.parentElement;
    const table = container?.querySelector("table");
    if (table instanceof HTMLTableElement) return table;
  }

  const tables = Array.from(document.querySelectorAll("table"));
  for (const table of tables) {
    const headerText = table.querySelector("thead")?.textContent ?? "";
    if (/ann\.?\s*roi/i.test(headerText) && /pop/i.test(headerText)) {
      return table;
    }
  }

  return null;
}

function ensureScoreCell(
  rowEl: HTMLTableRowElement,
  rubricCell: HTMLTableCellElement | null,
): HTMLTableCellElement {
  let cell = rowEl.querySelector<HTMLTableCellElement>(`.${SCORE_CELL}`);
  if (!cell) {
    cell = document.createElement("td");
    cell.className = SCORE_CELL;
    if (rubricCell) rowEl.insertBefore(cell, rubricCell);
    else rowEl.appendChild(cell);
  } else if (rubricCell && cell.nextElementSibling !== rubricCell) {
    rowEl.insertBefore(cell, rubricCell);
  }
  return cell;
}

export function rubricBandClass(text: string): string | null {
  const id = classifyRubricBand(text);
  if (id === "none") return null;
  if (id === "weak") return "sfe-score-band-weak";
  if (id === "higher") return "sfe-score-band-higher";
  if (id === "lower") return "sfe-score-band-lower";
  if (id === "moderate") return "sfe-score-band-moderate";
  return null;
}

export type RubricBandId = "higher" | "moderate" | "lower" | "weak" | "none";

export const RUBRIC_BAND_OPTIONS: ReadonlyArray<{ id: RubricBandId; label: string }> = [
  { id: "higher", label: "High" },
  { id: "moderate", label: "Moderate" },
  { id: "lower", label: "Lower" },
  { id: "weak", label: "Weak/flagged" },
  { id: "none", label: "No report" },
];

export function classifyRubricBand(text: string): RubricBandId {
  const t = text.toLowerCase();
  if (/no\s*report|missing|unavailable/.test(t)) return "none";
  // Match decision language used in the wheel rubric (Avoid / Selective / Small size).
  if (/avoid|weak|flagged/.test(t)) return "weak";
  if (/small|lower/.test(t)) return "lower";
  if (/selective|moderate/.test(t)) return "moderate";
  if (/higher|good|^high$/.test(t)) return "higher";
  return "none";
}

const SCORE_BAND_CLASSES = [
  "sfe-score-band-moderate",
  "sfe-score-band-higher",
  "sfe-score-band-lower",
  "sfe-score-band-weak",
] as const;

function parseRgb(css: string): [number, number, number] | null {
  const m = css.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

function colorDistance(a: [number, number, number], b: [number, number, number]): number {
  return Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) + Math.abs(a[2] - b[2]);
}

/** Pull the rubric band's accent color from the native cell (walks descendants
 * because the <td> itself is often white while the label span is colored). */
function extractRubricColor(rubricCell: HTMLElement): string | null {
  const row = rubricCell.closest("tr");
  const defaultRgb =
    parseRgb(getComputedStyle(row instanceof HTMLElement ? row : rubricCell).color) ??
    [232, 234, 237];

  let best = "";
  let bestDist = 0;
  const walk = (el: Element) => {
    if (!(el instanceof HTMLElement)) return;
    const rgb = parseRgb(getComputedStyle(el).color);
    if (rgb) {
      const dist = colorDistance(rgb, defaultRgb);
      if (dist > bestDist && dist > 20) {
        bestDist = dist;
        best = getComputedStyle(el).color;
      }
    }
    for (const child of el.children) walk(child);
  };
  walk(rubricCell);
  return best || null;
}

function syncScoreColorFromRubric(
  scoreCell: HTMLTableCellElement,
  rubricCell: HTMLTableCellElement | null,
  rubricText: string,
): void {
  scoreCell.classList.remove(...SCORE_BAND_CLASSES);
  scoreCell.style.removeProperty("color");

  const bandCls = rubricBandClass(rubricText);
  if (bandCls) scoreCell.classList.add(bandCls);

  const extracted = rubricCell ? extractRubricColor(rubricCell) : null;
  if (extracted) scoreCell.style.setProperty("color", extracted, "important");
}

/** number → score, null → "—" (no report), undefined → "…" (loading). */
function renderScoreCell(
  cell: HTMLTableCellElement,
  score: number | null | undefined,
  rubricCell: HTMLTableCellElement | null,
): void {
  if (typeof score === "number") {
    cell.textContent = `${score}/100`;
    cell.dataset.score = String(score);
    cell.classList.remove("sfe-score-cell-empty", "sfe-score-cell-loading");
    syncScoreColorFromRubric(cell, rubricCell, rubricCell?.textContent?.trim() ?? "");
  } else if (score === null) {
    cell.textContent = "—";
    delete cell.dataset.score;
    cell.classList.add("sfe-score-cell-empty");
    cell.classList.remove("sfe-score-cell-loading");
    cell.classList.remove(...SCORE_BAND_CLASSES);
    cell.style.removeProperty("color");
  } else {
    cell.textContent = "…";
    delete cell.dataset.score;
    cell.classList.add("sfe-score-cell-loading");
    cell.classList.remove("sfe-score-cell-empty");
    cell.classList.remove(...SCORE_BAND_CLASSES);
    cell.style.removeProperty("color");
  }
}

/** Paints every Score cell from the current cache (no network). Only "No
 * report" rubric bands get "—"; all others stay "…" until a score arrives. */
function paintScores(table: HTMLTableElement): void {
  for (const row of parseScreenerRows(table)) {
    const rubricCell = findBodyRubricCell(row.element, table);
    const cell = ensureScoreCell(row.element, rubricCell);
    const rubricText = rubricCell?.textContent?.trim() ?? row.rubricBand;
    syncNoReportRowClass(row.element, rubricText);

    if (/no\s*report/i.test(rubricText)) {
      renderScoreCell(cell, null, rubricCell);
      continue;
    }
    const cached = getCachedScore(row.ticker);
    renderScoreCell(cell, typeof cached === "number" ? cached : undefined, rubricCell);
  }
}

export function repaintScoreColumn(): void {
  const table = findScreenerTable();
  if (table) paintScores(table);
}

export function injectScoreColumn(table: HTMLTableElement): void {
  ensureScoreProvider();
  const headerRow = table.querySelector("thead tr");
  const rubricHeader = findRubricBandHeader(table);

  if (headerRow && !headerRow.querySelector(`.${SCORE_HEADER}`)) {
    const th = document.createElement("th");
    th.className = SCORE_HEADER;
    th.textContent = "Score";
    th.title = "Fundamental rubric score (0–100) from the report; — when no report";
    if (rubricHeader) headerRow.insertBefore(th, rubricHeader);
    else headerRow.insertBefore(th, headerRow.children[headerRow.children.length - 1]);
  } else if (headerRow && rubricHeader) {
    const existing = headerRow.querySelector(`.${SCORE_HEADER}`);
    if (existing && existing.nextElementSibling !== rubricHeader) {
      headerRow.insertBefore(existing, rubricHeader);
    }
  }

  const rows = parseScreenerRows(table);
  const need: string[] = [];

  for (const row of rows) {
    const rubricCell = findBodyRubricCell(row.element, table);
    ensureScoreCell(row.element, rubricCell);
  }

  const alignedRows = parseScreenerRows(table);
  for (const row of alignedRows) {
    const rubricCell = findBodyRubricCell(row.element, table);
    const rubricText = rubricCell?.textContent?.trim() ?? row.rubricBand;
    if (/no\s*report/i.test(rubricText)) continue;
    const cached = getCachedScore(row.ticker);
    if (typeof cached !== "number") need.push(row.ticker);
  }

  paintScores(table);

  if (need.length) {
    ensureScores(need, () => {
      // Repaint from cache as each score resolves; cheap and always targets
      // the live table (rows may have re-rendered since the request began).
      if (table.isConnected) paintScores(table);
    });
  }
}

export function removeScoreColumn(table: HTMLTableElement): void {
  table.querySelectorAll(`.${SCORE_HEADER}, .${SCORE_CELL}`).forEach((el) => el.remove());
}

function syncNoReportRowClass(row: HTMLTableRowElement, rubricText: string): void {
  row.classList.toggle("sfe-no-report-row", /no\s*report/i.test(rubricText));
}

/** Mark rows without a rubric report and suppress their left accent band. */
export function syncNoReportRowMarkers(table: HTMLTableElement): void {
  for (const row of parseScreenerRows(table)) {
    const rubricCell = findBodyRubricCell(row.element, table);
    const rubricText = rubricCell?.textContent?.trim() ?? row.rubricBand;
    syncNoReportRowClass(row.element, rubricText);
  }
}

export function applyRowHighlights(
  table: HTMLTableElement,
  minRoi: number,
  minPop: number,
): void {
  const rows = parseScreenerRows(table);
  for (const row of rows) {
    row.element.classList.remove("sfe-row-hot", "sfe-row-warm", "sfe-row-flagged");

    const rubricCell = findBodyRubricCell(row.element, table);
    const rubricText = rubricCell?.textContent?.trim() ?? row.rubricBand;
    syncNoReportRowClass(row.element, rubricText);

    if (/no\s*report/i.test(rubricText)) continue;

    if (/weak|flagged/i.test(rubricText)) {
      row.element.classList.add("sfe-row-flagged");
      continue;
    }

    if (row.annRoi >= minRoi && row.pop >= minPop) {
      row.element.classList.add("sfe-row-hot");
    } else if (row.annRoi >= minRoi * 0.75 || row.pop >= minPop * 0.9) {
      row.element.classList.add("sfe-row-warm");
    }
  }
}

export function clearRowHighlights(table: HTMLTableElement): void {
  table.querySelectorAll("tbody tr").forEach((row) => {
    row.classList.remove("sfe-row-hot", "sfe-row-warm", "sfe-row-flagged");
  });
}

export function applyCompactMode(table: HTMLTableElement, enabled: boolean): void {
  const hideIndices = [1, 4, 8]; // Sector, Target, Mid/Last
  const rows = table.querySelectorAll("thead tr, tbody tr");
  rows.forEach((row) => {
    Array.from(row.children).forEach((cell, index) => {
      if (hideIndices.includes(index)) {
        (cell as HTMLElement).classList.toggle("sfe-hidden-col", enabled);
      }
    });
  });
}

export function exportVisibleRows(table: HTMLTableElement): void {
  const rows = parseScreenerRows(table);
  const headers = [
    "Ticker",
    "Sector",
    "Strike",
    "Price",
    "Target",
    "Rubric Band",
    "Expiry",
    "DTE",
    "Collateral",
    "Ann ROI",
    "POP",
    "Held",
    "Score",
  ];

  const lines = [
    headers.join(","),
    ...rows.map((r) =>
      [
        r.ticker,
        r.sector,
        r.strike,
        r.price,
        r.target,
        `"${r.rubricBand}"`,
        r.expiry,
        r.dte,
        r.collateral,
        r.annRoi,
        r.pop,
        r.held,
        computeScore(r.annRoi, r.pop),
      ].join(","),
    ),
  ];

  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `stock-forge-screener-${new Date().toISOString().slice(0, 10)}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
  showToast(`Exported ${rows.length} screener row(s).`, "success");
}
